import type { Activity } from "../types";

interface FormattedActivity {
  icon: string;
  message: string;
  color: string;
}

const LABEL_MAP: Record<string, { icon: string; color: string; template: (p: Record<string, unknown>) => string }> = {
  created: {
    icon: "Plus",
    color: "text-emerald-400",
    template: (p) => `Card criado${p.title ? `: "${p.title}"` : ""}`,
  },
  moved: {
    icon: "ArrowRight",
    color: "text-blue-400",
    template: (p) => `Movido${p.from != null ? ` da raia #${p.from}` : ""} para raia #${p.to}`,
  },
  chat_moved: {
    icon: "ArrowRight",
    color: "text-cyan-400",
    template: (p) => `[IAGO] Moveu para raia #${p.to}`,
  },
  completed: {
    icon: "Check",
    color: "text-emerald-400",
    template: () => "Card concluido",
  },
  reopened: {
    icon: "RotateCcw",
    color: "text-amber-400",
    template: () => "Card reaberto",
  },
  description_updated: {
    icon: "FileText",
    color: "text-violet-400",
    template: () => "Descricao alterada",
  },
  title_updated: {
    icon: "Edit",
    color: "text-violet-400",
    template: (p) => `Titulo alterado: "${p.prev}" → "${p.new}"`,
  },
  priority_updated: {
    icon: "AlertTriangle",
    color: "text-orange-400",
    template: (p) => `Prioridade alterada: ${p.prev} → ${p.new}`,
  },
  due_date_updated: {
    icon: "Calendar",
    color: "text-yellow-400",
    template: (p) => {
      const prev = p.prev || "sem prazo";
      const next = p.new || "sem prazo";
      return `Prazo alterado: ${prev} → ${next}`;
    },
  },
  duplicated: {
    icon: "Copy",
    color: "text-slate-400",
    template: (p) => `Card duplicado${p.source_id ? ` do #${p.source_id}` : ""}`,
  },
  commented: {
    icon: "MessageSquare",
    color: "text-blue-400",
    template: () => "Comentario adicionado",
  },
  ai_created: {
    icon: "Sparkles",
    color: "text-cyan-400",
    template: () => "[IAGO] Card criado por voz",
  },
  ai_refined: {
    icon: "Wand2",
    color: "text-cyan-400",
    template: () => "[IAGO] Card refinado",
  },
  updated: {
    icon: "Settings",
    color: "text-slate-400",
    template: (p) => {
      const keys = (p.keys as string[]) || [];
      const fieldMap: Record<string, string> = {
        title: "titulo", description: "descricao", priority: "prioridade",
        effort: "complexidade", dueDate: "prazo", laneId: "raia",
        mood: "mood", whyItMatters: "proposito", notes: "notas",
        categoryIds: "categorias", subtasks: "subtarefas", resources: "referencias",
        position: "posicao", archived: "arquivamento",
      };
      const labels = keys.map((k: string) => fieldMap[k] || k);
      return `Alterados: ${labels.join(", ")}`;
    },
  },
  chat_priority: {
    icon: "AlertTriangle",
    color: "text-cyan-400",
    template: (p) => `[IAGO] Prioridade alterada para ${p.priority}`,
  },
  chat_mood: {
    icon: "Heart",
    color: "text-cyan-400",
    template: (p) => `[IAGO] Mood alterado para ${p.mood}`,
  },
  chat_subtask_added: {
    icon: "CheckSquare",
    color: "text-cyan-400",
    template: (p) => `[IAGO] Subtarefa adicionada: "${p.title}"`,
  },
  attachment_added: {
    icon: "Paperclip",
    color: "text-violet-400",
    template: (p) => `Anexo adicionado: "${p.name}"`,
  },
  attachment_removed: {
    icon: "Paperclip",
    color: "text-rose-400",
    template: (p) => `Anexo removido: "${p.name}"`,
  },
};

const FALLBACK: FormattedActivity = {
  icon: "Circle",
  color: "text-slate-500",
  message: "Evento desconhecido",
};

export function formatActivity(a: Activity, laneById: Record<number, string>): FormattedActivity {
  const def = LABEL_MAP[a.kind];
  if (!def) return { ...FALLBACK, message: `${a.kind}: ${JSON.stringify(a.payload)}` };

  let message = def.template(a.payload as Record<string, unknown>);

  // substitui ids de raia por nomes
  if ((a.kind === "moved" || a.kind === "chat_moved") && a.payload) {
    const p = a.payload as Record<string, unknown>;
    const fromName = p.from != null ? laneById[p.from as number] || `#${p.from}` : null;
    const toName = p.to != null ? laneById[p.to as number] || `#${p.to}` : null;
    if (a.kind === "moved") {
      message = `Movido${fromName ? ` de "${fromName}"` : ""} para "${toName || `#${p.to}`}"`;
    } else {
      message = `[IAGO] Moveu para "${toName || `#${p.to}`}"`;
    }
  }

  return { icon: def.icon, color: def.color, message };
}

/** Conjunto de icones usados pela timeline. Mapeia nome → componente lucide. */
export const ACTIVITY_ICONS = [
  "Plus", "ArrowRight", "Check", "RotateCcw", "FileText", "Edit",
  "AlertTriangle", "Calendar", "Copy", "MessageSquare", "Sparkles",
  "Wand2", "Settings", "Heart", "CheckSquare", "Circle",
] as const;
